/**
 * ParagraphMetadataMapper - Utility for mapping paragraph IDs to indices
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for mapping paragraph IDs to their indices
 * in a document, which is useful for calculating spill frontiers and tracking
 * affected paragraphs.
 */

export interface ParagraphMetadata {
  id: string;
  para_ix: number;
}

export interface ParagraphInput {
  id: string;
  [key: string]: any;
}

/**
 * Map paragraph IDs to their indices in a document
 * 
 * Creates an array of metadata objects containing paragraph IDs and their indices.
 * Paragraphs not found in the document are filtered out.
 * 
 * @param paragraphIds - Array of paragraph IDs to map
 * @param documentParagraphs - Array of paragraph objects from the document
 * @returns Array of paragraph metadata with id and para_ix
 */
export function mapParagraphIdsToIndices(
  paragraphIds: string[],
  documentParagraphs: ParagraphInput[]
): ParagraphMetadata[] {
  return paragraphIds
    .map(id => {
      const index = documentParagraphs.findIndex(p => p.id === id);
      return index >= 0 ? { id, para_ix: index } : null;
    })
    .filter((m): m is ParagraphMetadata => m !== null);
}

/**
 * Find the minimum paragraph index from metadata
 * 
 * @param metadata - Array of paragraph metadata
 * @returns Minimum para_ix, or null if array is empty
 */
export function findMinParagraphIndex(metadata: ParagraphMetadata[]): number | null {
  if (metadata.length === 0) {
    return null;
  }
  
  return Math.min(...metadata.map(m => m.para_ix));
}

/**
 * Find paragraph ID with minimum index
 * 
 * @param metadata - Array of paragraph metadata
 * @returns Paragraph ID with minimum para_ix, or null if array is empty
 */
export function findParagraphIdWithMinIndex(metadata: ParagraphMetadata[]): string | null {
  if (metadata.length === 0) {
    return null;
  }
  
  const minParaIx = findMinParagraphIndex(metadata);
  if (minParaIx === null) {
    return null;
  }
  
  const found = metadata.find(m => m.para_ix === minParaIx);
  return found ? found.id : null;
}

